import React from 'react'
import { Tooltip, Typography } from 'antd'
import { Link } from 'react-router'
import { EmployeeCard } from '@/common/components/EmployeeCard.tsx'
import { cn } from '@/utils/style.ts'
import { useEmployeePublicInfo } from '@/common/hooks/useEmployeePublicInfo.tsx'
import { userRoutes } from '@/utils/routes.ts'
import { EmployeePublicInfo } from '@/features/employee-data/employee-data.def.ts'

export type EmployeeTooltipProps = {
  employeeId?: string | null
  className?: string
  disableLink?: boolean
  showNotFound?: boolean
  labelFn?: (employee: EmployeePublicInfo) => React.ReactNode
}

export const EmployeeTooltip: React.FC<EmployeeTooltipProps> = ({
  employeeId,
  className,
  disableLink = false,
  showNotFound = false,
  labelFn,
}) => {
  const { getEmployeePublicInfo } = useEmployeePublicInfo()
  const employee = getEmployeePublicInfo(employeeId)
  const employeeNameNode = (labelFn && employee) ? labelFn(employee) : (employee?.displayName || (showNotFound ? 'Not Found' : employeeId))

  const textNode = typeof employeeNameNode === 'string' ? (
    <Typography.Text
      className={cn('font-semibold text-link cursor-pointer', className)}
    >
      {employeeNameNode}
    </Typography.Text>
  ) : employeeNameNode

  if (!employeeId) return null

  // For HC, always link to employee data page. For non-HC, link to employee profile if the feature is enabled, otherwise link to employee data page.
  const url = userRoutes.employeeProfile(employeeId)

  return (
    <Tooltip title={<EmployeeCard employeeId={employeeId} className="border-none" />} color="white">
      {(disableLink || !url) ? textNode : (
        <Link to={url} target="_blank" className={className}>
          {textNode}
        </Link>
      )}
    </Tooltip>
  )
}

type UsernameTooltipProps = {
  username?: string
  className?: string
}

export const UsernameTooltip: React.FC<UsernameTooltipProps> = ({
  username,
  className,
}) => {
  const { getEmployeePublicInfoByUsername } = useEmployeePublicInfo()
  const employee = getEmployeePublicInfoByUsername(username)

  const employeeId = employee?.employeeId
  if (!employeeId) return null

  return (
    <Tooltip title={<EmployeeCard employeeId={employeeId} className="border-none" />} color="white">
      <Typography.Text className={cn('font-semibold text-link', className)}>
        {employee.displayName}
      </Typography.Text>
    </Tooltip>
  )
}